import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Collapse,
  TextField,
  Button,
  Typography,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import MobileListCard from '../components/MobileListCard';
import EmptyState from '../components/EmptyState';
import { formatInBusinessTimezone, getBusinessToday, toBusinessTimezone } from '../utils/dateTimeHelper';
import { parseISO, isBefore, startOfDay } from 'date-fns';
import StatusBadge from '../components/StatusBadge';

const SUBJECT_CHIP_PALETTE = [
  { bg: '#E3F2FD', color: '#1565C0' },
  { bg: '#E8F5E9', color: '#2E7D32' },
  { bg: '#F3E5F5', color: '#7B1FA2' },
  { bg: '#FFF3E0', color: '#E65100' },
  { bg: '#E0F7FA', color: '#00838F' },
  { bg: '#FCE4EC', color: '#C2185B' },
  { bg: '#F1F8E9', color: '#558B2F' },
  { bg: '#EDE7F6', color: '#4527A0' },
];

const hashSubjectPalette = (name) => {
  let h = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i += 1) {
    h = s.charCodeAt(i) + ((h << 5) - h);
  }
  return SUBJECT_CHIP_PALETTE[Math.abs(h) % SUBJECT_CHIP_PALETTE.length];
};

const defaultFilter = () => ({ from: '', to: '', mode: 'posted' });

const AssignmentScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [appliedFilter, setAppliedFilter] = useState(defaultFilter);
  const [draftFilter, setDraftFilter] = useState(defaultFilter);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  const fetchPage = useCallback(async (page, filter) => {
    const token = localStorage.getItem('userToken');
    const qs = new URLSearchParams({
      PageSize: '10',
      PageNumber: String(page),
    });
    if (filter.from) qs.set('fromDate', filter.from);
    if (filter.to) qs.set('toDate', filter.to);
    if (filter.from || filter.to) qs.set('dateFilterType', filter.mode);

    const response = await api.get(
      `api/school-common/assignments/get-assignment-list-student?${qs.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data.data;
  }, []);

  const loadData = useCallback(
    async (page = 1, filter = appliedFilter) => {
      try {
        setLoading(true);
        const data = await fetchPage(page, filter);
        if (page === 1) {
          setItems(data);
        } else {
          setItems((prevItems) => [...prevItems, ...data]);
        }
        setHasMore(data.length === 10);
        setPageNumber(page + 1);
      } catch (error) {
        handleCatch(error);
      } finally {
        setLoading(false);
      }
    },
    [appliedFilter, fetchPage]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchPage(1, defaultFilter());
        if (!alive) return;
        setItems(data);
        setHasMore(data.length === 10);
        setPageNumber(2);
      } catch (error) {
        handleCatch(error);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [fetchPage]);

  const onRefresh = useCallback(async () => {
    setHasMore(true);
    setPageNumber(1);
    await loadData(1, appliedFilter);
  }, [appliedFilter, loadData]);

  const onView = (item) => {
    navigate('/assignment-submit', { state: { item } });
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      loadData(pageNumber, appliedFilter);
    }
  };

  const handleApplyFilter = async () => {
    const next = { ...draftFilter };
    setAppliedFilter(next);
    setHasMore(true);
    setPageNumber(1);
    try {
      setLoading(true);
      const data = await fetchPage(1, next);
      setItems(data);
      setHasMore(data.length === 10);
      setPageNumber(2);
    } catch (error) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilter = async () => {
    const cleared = defaultFilter();
    setDraftFilter(cleared);
    setAppliedFilter(cleared);
    setHasMore(true);
    setPageNumber(1);
    try {
      setLoading(true);
      const data = await fetchPage(1, cleared);
      setItems(data);
      setHasMore(data.length === 10);
      setPageNumber(2);
    } catch (error) {
      handleCatch(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFilterPanel = () => {
    setFilterPanelOpen((open) => {
      const willOpen = !open;
      if (willOpen) setDraftFilter(appliedFilter);
      return willOpen;
    });
  };

  const postedTime = (item) => item.creationTime || item.updatedDate;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Assignments"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box
        onClick={toggleFilterPanel}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          mx: 1,
          mt: 1,
          borderRadius: 1,
          backgroundColor: '#fff',
          cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ fontSize: 20, color: colors.BLUE }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1A1A2E' }}>
            Filter by date
          </Typography>
          {(appliedFilter.from || appliedFilter.to) && (
            <Chip
              label="Active"
              size="small"
              sx={{ height: 22, fontSize: '11px', fontWeight: 600 }}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: 24,
            color: '#888',
            transform: filterPanelOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </Box>

      <Collapse in={filterPanelOpen}>
        <Box
          sx={{
            mx: 1,
            mb: 1,
            p: 2,
            backgroundColor: '#fff',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            borderTop: '1px solid #eee',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography sx={{ fontSize: 12, color: '#666', mb: 0.5 }}>Date applies to</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={draftFilter.mode}
            onChange={(_, v) => v && setDraftFilter((f) => ({ ...f, mode: v }))}
            sx={{ mb: 1.5, display: 'flex' }}
          >
            <ToggleButton value="posted" sx={{ flex: 1, textTransform: 'none', fontSize: 13 }}>
              Posted
            </ToggleButton>
            <ToggleButton value="due" sx={{ flex: 1, textTransform: 'none', fontSize: 13 }}>
              Due
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={draftFilter.from}
              onChange={(e) => setDraftFilter((f) => ({ ...f, from: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 140px', minWidth: 140 }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={draftFilter.to}
              onChange={(e) => setDraftFilter((f) => ({ ...f, to: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 140px', minWidth: 140 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={handleApplyFilter} disabled={loading}>
              Apply
            </Button>
            <Button variant="outlined" size="small" onClick={handleClearFilter} disabled={loading}>
              Clear
            </Button>
          </Box>
        </Box>
      </Collapse>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#F5F7FA',
        }}
      >
        {loading && items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<AssignmentIcon />}
            iconColor="#43A047"
            title="No Assignments"
            subtitle={
              appliedFilter.from || appliedFilter.to
                ? 'No assignments in this date range. Try changing or clearing the filter.'
                : 'No assignments have been posted yet.'
            }
            actionLabel="Retry"
            onAction={() => loadData(1, appliedFilter)}
          />
        ) : (
          <Box
            sx={{
              backgroundColor: '#fff',
              mt: 1,
              mx: 1,
              borderRadius: '12px 12px 0 0',
              overflow: 'hidden',
            }}
          >
            {items.map((item, index) => {
              const subj = (item.subjectName || '').trim() || 'General';
              const chipStyle = (item.subjectName || '').trim()
                ? hashSubjectPalette(item.subjectName)
                : { bg: '#ECEFF1', color: '#546E7A' };
              const created = postedTime(item);

              return (
                <MobileListCard
                  key={item.id}
                  icon={<AssignmentIcon />}
                  iconColor="#43A047"
                  iconBg="rgba(67,160,71,0.1)"
                  title={item.title}
                  subtitleNode={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={subj}
                        size="small"
                        sx={{
                          alignSelf: 'flex-start',
                          height: 22,
                          fontSize: '11px',
                          fontWeight: 700,
                          backgroundColor: chipStyle.bg,
                          color: chipStyle.color,
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ fontSize: '12px', color: '#555555', lineHeight: 1.35 }}
                      >
                        Posted: {formatInBusinessTimezone(created, 'dd MMM yyyy, h:mm a')}
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ fontSize: '12px', color: '#666666', lineHeight: 1.35 }}
                      >
                        Due: {formatInBusinessTimezone(item.toBeSubmittedOn, 'dd MMM yyyy')}
                      </Typography>
                    </Box>
                  }
                  description={`By ${item.teacherName || ''}`}
                  rightIcon={
                    <StatusBadge
                      status={
                        isBefore(
                          startOfDay(toBusinessTimezone(parseISO(item.toBeSubmittedOn))),
                          getBusinessToday()
                        )
                          ? 'overdue'
                          : 'pending'
                      }
                    />
                  }
                  onClick={() => onView(item)}
                  showDivider={index < items.length - 1}
                  showChevron
                />
              );
            })}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Box
                    onClick={loadMore}
                    sx={{
                      fontSize: 14,
                      color: colors.BLUE,
                      cursor: 'pointer',
                      fontWeight: 500,
                      py: 1,
                    }}
                  >
                    Load more
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AssignmentScreen;
